import {Component, ElementRef, forwardRef} from '@angular/core';
import {ControlValueAccessor, NG_VALUE_ACCESSOR} from "@angular/forms";
import {SubscribedComponent} from "../../subscribed.component";
import {GoogleMapsService} from "../../shared/google-maps.service";
import {Globals} from "../../shared/Globals";

export interface Overlay{
  type: google.maps.drawing.OverlayType.CIRCLE|google.maps.drawing.OverlayType.POLYGON|google.maps.drawing.OverlayType.RECTANGLE,
  points?:{lat: number, lng: number}[]|{north:number, south:number, east: number, west:number},
  center?:{lat: number, lng:number},
  radius?: number
}

@Component({
    selector: 'bst-map-editor',
    templateUrl: './map-editor.component.html',
    styleUrls: ['./map-editor.component.scss'],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => MapEditorComponent),
            multi: true
        }
    ],
    standalone: false
})
export class MapEditorComponent extends SubscribedComponent implements ControlValueAccessor{

  private map: google.maps.Map;
  private drawing: google.maps.drawing.DrawingManager;
  private overlays = [];

  private readonly styleoptions = {
    clickable: false,
    fillColor: '#8eabd7',
    fillOpacity: 0.55,
    strokeColor: '#453f47'
  };

  constructor(private gmapsService: GoogleMapsService,
              private me: ElementRef) {
    super();
  }

  onChange = (value: Overlay[]) => {};
  onTouched = () => {};

  ngOnInit(): void {
    super.ngOnInit();
  }

  ngOnDestroy() {
    super.ngOnDestroy();
    if(this.drawing){
      google.maps.event.clearListeners(this.drawing, 'overlaycomplete');
      this.drawing = null;
    }
  }

  private async bootstrap() {
    if(!this.map) {
      this.loading.next(true);
      // The Maps script is only fetched once a map is shown (#1755).
      if (!await this.gmapsService.load()) {
        this.loading.next(false);
        return;
      }
      const loc = await Globals.getLocation();
      this.map = new google.maps.Map(this.me.nativeElement, {
        center: loc || {lat: 0, lng: 0},
        zoom: loc ? 10 : 3,
        fullscreenControl: false,
        streetViewControl: false,
        keyboardShortcuts: false,
        mapTypeControl: false,
        mapId: '8d0b1b219e9a6c21'
      });

      this.enabled();

      this.loading.next(false);
    }
  }

  private enabled(){
    // Before bootstrap() the Maps script may not be loaded yet; bootstrap() calls this once it is.
    if(!this.drawing && this.map){
      this.drawing = new google.maps.drawing.DrawingManager({
        drawingMode: null,
        drawingControl: true,
        drawingControlOptions: {
          position: google.maps.ControlPosition.TOP_CENTER,
          drawingModes: [google.maps.drawing.OverlayType.CIRCLE]
        },
        circleOptions: this.styleoptions,
        map: this.map
      });

      google.maps.event.addListener(this.drawing, 'overlaycomplete', (drawing) => {
        this.overlays.push(drawing);
        this.onChange(this.encoded);
      });
    }
  }

  private disable(){
    if(this.drawing){
      google.maps.event.clearListeners(this.drawing, 'overlaycomplete');
      this.drawing.setMap(null);
      this.drawing = null;
    }
  }

  private get encoded(): Overlay[]{
    const encoded = [];
    for(let o of this.overlays){
      const encode: Overlay = {
        type: o.type
      };

      if(o.type === google.maps.drawing.OverlayType.CIRCLE){
        encode.center = {lat: +(o.overlay.center.lat().toFixed(6)), lng: +(o.overlay.center.lng().toFixed(6))};
        encode.radius = +(o.overlay.radius.toFixed(6));
      }
      else if(o.type === google.maps.drawing.OverlayType.RECTANGLE){
        encode.points = o.overlay.bounds.toJSON();
        for(let j in encode.points){
          if(encode.points.hasOwnProperty(j)) {
            encode.points[j] = +(encode.points[j].toFixed(6));
          }
        }
      }
      else{
        const path = o.overlay.getPath()
        const coordinates = [];

        for (let i = 0 ; i < path.length ; i++) {
          coordinates.push({
            lat: +(path.getAt(i).lat().toFixed(6)),
            lng: +(path.getAt(i).lng().toFixed(6))
          });
        }
        encode.points = coordinates;
      }
      encoded.push(encode);
    }

    return encoded;
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    if(!isDisabled){
      this.enabled()
    }
    else{
      this.disable();
    }
  }

  writeValue(value: Overlay[]): void {
    this.bootstrap().then(() => {
      if (!this.map) { return; }
      if (value && value instanceof Array) {
        this.overlays = new Array(value.length);
        const bounds = new google.maps.LatLngBounds();
        for (let i in value) {
          const eoverlay = value[i];
          const uncoded = {type: eoverlay.type, overlay: null};
          if (eoverlay.type === google.maps.drawing.OverlayType.RECTANGLE) {
            const rectangle = new google.maps.Rectangle(Object.assign(this.styleoptions, {
              map: this.map,
              bounds: <google.maps.LatLngBounds><unknown>eoverlay.points
            }));
            uncoded.overlay = rectangle;
            bounds.union(rectangle.getBounds());
          } else if (eoverlay.type === google.maps.drawing.OverlayType.CIRCLE) {
            const circle = new google.maps.Circle(Object.assign(this.styleoptions, {
              map: this.map,
              center: eoverlay.center,
              radius: eoverlay.radius
            }));
            uncoded.overlay = circle;
            bounds.union(circle.getBounds());
          } else { // polygon
            const polygon = new google.maps.Polygon(Object.assign(this.styleoptions, {
              map: this.map,
              paths: <google.maps.LatLng[]><unknown>eoverlay.points
            }));
            uncoded.overlay = polygon;
            polygon.getPath().forEach(latlng => {
              bounds.extend(latlng);
            });
          }
          this.overlays[i] = uncoded;
        }
        this.map.fitBounds(bounds);
      }
      this.onChange(this.encoded);
    });
  }

}
